import { useState } from 'react';
import PageHeader from '@/components/base/PageHeader';
import { useData } from '@/context/DataContext';
import { formatDate } from '@/lib/format';

const weekdayLabels = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];

const eventColors: Record<string, string> = {
  Servis: 'bg-accent-100 text-accent-800',
  Görev: 'bg-primary-100 text-primary-800',
  Teklif: 'bg-secondary-100 text-secondary-700',
  Tahsilat: 'bg-primary-500 text-white',
  Teslim: 'bg-primary-100 text-primary-800',
  'Satın Alma': 'bg-secondary-100 text-secondary-700',
};

const monthNames = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

function isoDate(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

export default function Calendar() {
  const { calendarEvents } = useData();
  const [viewDate, setViewDate] = useState(() => new Date(2026, 7, 1));
  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7; // Monday = 0
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = isoDate(2026, 7, 12);

  const cells: (number | null)[] = [
    ...Array.from({ length: firstWeekday }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const prev = () => setViewDate(new Date(year, month - 1, 1));
  const next = () => setViewDate(new Date(year, month + 1, 1));

  return (
    <div className="space-y-5 animate-fade-in-up">
      <PageHeader title="Takvim" description="Teklif, teslim, görev, tahsilat ve servis işlemlerini tek takvimde görün." />

      <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
        <div className="flex items-center justify-between border-b border-background-200 px-5 py-4">
          <h2 className="font-heading text-base font-semibold text-foreground-950">{monthNames[month]} {year}</h2>
          <div className="flex items-center gap-2">
            <button type="button" onClick={prev} className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100">
              <i className="ri-arrow-left-s-line" />
            </button>
            <button type="button" onClick={() => setViewDate(new Date(2026, 7, 1))} className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm font-semibold text-foreground-700 hover:bg-background-100">
              Bugün
            </button>
            <button type="button" onClick={next} className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:bg-background-100">
              <i className="ri-arrow-right-s-line" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 border-b border-background-200 bg-background-100/50">
          {weekdayLabels.map((d) => (
            <div key={d} className="px-2 py-2.5 text-center text-xs font-semibold uppercase tracking-wide text-foreground-500">{d}</div>
          ))}
        </div>

        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            if (day === null) return <div key={`empty-${idx}`} className="min-h-[110px] border-b border-r border-background-100 bg-background-100/20" />;
            const date = isoDate(year, month, day);
            const events = calendarEvents.filter((e) => e.date === date);
            const isToday = date === today;
            return (
              <div key={date} className={`min-h-[110px] border-b border-r border-background-100 p-1.5 ${isToday ? 'bg-primary-100/40' : ''}`}>
                <div className="flex items-center justify-between">
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${isToday ? 'bg-primary-500 text-white' : 'text-foreground-700'}`}>{day}</span>
                </div>
                <div className="mt-1 space-y-1">
                  {events.slice(0, 3).map((e) => (
                    <button key={e.id} type="button" className={`block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] font-semibold cursor-pointer ${eventColors[e.type] ?? 'bg-secondary-100 text-secondary-700'}`}>
                      {e.title}
                    </button>
                  ))}
                  {events.length > 3 && <p className="px-1 text-[10px] text-foreground-400">+{events.length - 3} daha</p>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3 px-1 text-sm">
        <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Etiketler:</span>
        {Object.keys(eventColors).map((k) => (
          <span key={k} className={`rounded-full px-2.5 py-1 text-xs font-semibold ${eventColors[k]}`}>{k}</span>
        ))}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50">
        <div className="border-b border-background-200 px-5 py-4">
          <h2 className="font-heading text-base font-semibold text-foreground-950">Yaklaşan İşlemler</h2>
        </div>
        <div className="divide-y divide-background-100">
          {[...calendarEvents].sort((a, b) => (a.date > b.date ? 1 : -1)).filter((e) => e.date >= today).slice(0, 8).map((e) => (
            <div key={e.id} className="flex items-center gap-3 px-5 py-3">
              <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${eventColors[e.type] ?? 'bg-secondary-100 text-secondary-700'}`}>{e.type}</span>
              <span className="min-w-0 flex-1 text-sm font-medium text-foreground-900">{e.title}</span>
              <span className="hidden text-xs text-foreground-500 sm:block">{e.related}</span>
              <span className="text-sm font-semibold text-foreground-700">{formatDate(e.date)}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}